import { useState } from "react";
import { estimateFromIngredients } from "../../../network/service/NutritionService";
import { MICRONUTRIENTS, SOURCE_NAMES } from "./nutrition-model";

const FIELD_NAMES = {
  fiber: "fibre",
  sugar: "sugars",
  saturatedFat: "saturated fat",
  transFat: "trans fat",
  addedSugar: "added sugars",
  sodium: "sodium",
  cholesterol: "cholesterol",
  ...Object.fromEntries(MICRONUTRIENTS.map(([code, name]) => [code, name])),
};
const fieldName = (f) => FIELD_NAMES[f.replace("micronutrients.", "")] ?? f;

/**
 * "Work out from the ingredients": for a mixed product (masala, ready mix,
 * home-style dish) whose ingredient list gives each ingredient's share.
 * Each ingredient is looked up as the raw food and the values added up.
 */
function IngredientEstimate({ ingredients, product, onUse }) {
  const [state, setState] = useState({ state: "idle" });

  const run = async () => {
    setState({ state: "busy" });
    try {
      setState({ state: "done", ...(await estimateFromIngredients({ ingredients, ...product })) });
    } catch (e) {
      setState({ state: "error", error: e.message });
    }
  };

  const { estimate, parts = [] } = state;
  return (
    <div className="nutri-estimate">
      <div className="nutri-row">
        <button type="button" className="btn btn-sm btn-white-bg" disabled={!ingredients.trim() || state.state === "busy"} onClick={run}>
          {state.state === "busy" ? "Working out…" : "Work out nutrition from these ingredients"}
        </button>
        <span className="nutri-muted">
          For mixes and home-style products: give each ingredient's % or weight, e.g. "Rice 60%, Moong dal 30%, Ghee
          5%, Salt" (one may be left as the rest).
        </span>
      </div>

      {state.state === "error" && <div className="nutri-status nutri-status-error">{state.error}</div>}

      {state.state === "done" && (
        <>
          <ul className="nutri-estimate-parts">
            {parts.map((p) => (
              <li key={p.name}>
                <strong>{p.percent}%</strong> {p.name}:{" "}
                {p.match ? (
                  <a href={p.match.url} target="_blank" rel="noreferrer">
                    {p.match.name} ({SOURCE_NAMES[p.match.source]})
                  </a>
                ) : (
                  <span className="nutri-different">
                    not found as this exact food
                    {p.possible.length > 0 && ` (closest: ${p.possible.join(", ")})`}; rename it or enter values by hand
                  </span>
                )}
              </li>
            ))}
          </ul>
          {estimate ? (
            <div className="nutri-status nutri-status-ok">
              <strong>
                Per 100 g: {estimate.values.calories} kcal, {estimate.values.protein} g protein, {estimate.values.carbs} g
                carbohydrates, {estimate.values.fat} g fat
              </strong>
              {estimate.unknown.length > 0 && (
                <div>
                  Unknown: {estimate.unknown.map((u) => `${fieldName(u.field)} (not on record for ${u.missingIn.join(", ")})`).join("; ")}.
                </div>
              )}
              {estimate.assumedZero.length > 0 && (
                <div className="nutri-muted">Counted as none (small amounts): {estimate.assumedZero.join("; ")}.</div>
              )}
              <div className="nutri-muted">
                Added up from the raw ingredients. Cooking or drying changes the water content, so check a cooked dish
                before marking it checked.
              </div>
              <button type="button" className="btn btn-sm btn-purple-bg mt-2" onClick={() => onUse(estimate)}>
                Use these values
              </button>
            </div>
          ) : (
            <div className="nutri-status nutri-status-warn">
              Every ingredient must be found before the values can be added up.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default IngredientEstimate;
